<template>
  <v-container fluid class="max-width">
    <v-card>
      <v-toolbar color="secondary" flat dense>
        <v-toolbar-title>Profit Margin Summary</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn small text to="/export-profit-margin-report">
          <v-icon left>mdi-keyboard-backspace</v-icon>Back
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-row dense class="mt-2">
          <!-- datepicker 1 -->
          <v-col cols="12" xs="12" md="3">
            <v-menu v-model="menu1" :close-on-content-click="false" :nudge-right="40" transition="scale-transition"
              offset-y min-width="auto">
              <template v-slot:activator="{ on }">
                <v-text-field dense outlined single-line v-model="StartDate" label="From"
                  prepend-icon="mdi-calendar-month" v-on="on" readonly></v-text-field>
              </template>
              <v-date-picker v-model="StartDate" @input="onStartDateChange"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- datepicker 2 -->
          <v-col cols="12" xs="12" md="3">
            <v-menu v-model="menu2" :close-on-content-click="false" :nudge-right="40" transition="scale-transition"
              offset-y>
              <template v-slot:activator="{ on }">
                <v-text-field dense outlined single-line v-model="EndDate" label="To"
                  prepend-icon="mdi-calendar-month" v-on="on" readonly></v-text-field>
              </template>
              <v-date-picker v-model="EndDate" @input="onEndDateChange"></v-date-picker>
            </v-menu>
          </v-col>

          <v-spacer></v-spacer>

          <!-- Export Button -->
          <v-col cols="12" xs="12" md="3" class="text-right">
            <download-excel :data="dailySales" :fields="fields" name="profit report">
              <v-btn small color="success">
                <v-icon left>mdi-file-excel</v-icon>Export to Excel
              </v-btn>
            </download-excel>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Profit Table -->
        <v-row class="subtitle-2 text--primary mt-4">
          <v-col cols="12">
            <v-data-table
              :headers="headers"
              :items="dailySales"
              :items-per-page="100"
              class="elevation-1">

              <template v-slot:item.Date="{ item }">
                <span class="black--text font-weight-medium">{{ item.Date | date }}</span>
              </template>

              <template v-slot:item.CustomerName="{ item }">
                <span class="black--text font-weight-medium">{{ item.CustomerName }}</span>
              </template>

              <template v-slot:item.ConvertedTotal="{ item }">
                <span class="black--text font-weight-medium">Ksh {{ item.ConvertedTotal | currency }}</span>
              </template>

              <template v-slot:item.TotalGeneralCost="{ item }">
                <span class="black--text font-weight-medium">Ksh {{ item.TotalGeneralCost | currency }}</span>
              </template>

              <template v-slot:item.Margin="{ item }">
                <span class="black--text font-weight-bold success--text text--darken-3">
                  Ksh {{ (item.ConvertedTotal - item.TotalGeneralCost) | currency }}
                </span>
              </template>

              <template v-slot:item.ProfitPercentage="{ item }">
                <v-chip small :color="getProfitColor(item)" dark>
                  {{ calculateProfitPercentage(item) }}%
                </v-chip>
              </template>

            </v-data-table>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Total Profit Summary -->
        <v-row>
          <v-col cols="12" md="6" lg="5">
            <v-card outlined elevation="1">
              <v-card-title class="secondary py-2">
                <span class="subtitle-1 font-weight-bold">Profit Summary</span>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="pa-3">
                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody>
                      <tr>
                        <td class="font-weight-bold black--text">Total Sales:</td>
                        <td class="font-weight-bold black--text text-right">
                          Ksh {{ totalSales | currency }}
                        </td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold black--text">Total Cost:</td>
                        <td class="font-weight-bold black--text text-right">
                          Ksh {{ totalCost | currency }}
                        </td>
                      </tr>
                      <tr class="success lighten-4">
                        <td class="font-weight-bold black--text">Margin:</td>
                        <td class="font-weight-bold success--text text--darken-3 text-right">
                          Ksh {{ totalProfitMargin | currency }}
                        </td>
                      </tr>
                      <tr class="success lighten-4">
                        <td class="font-weight-bold black--text">Profit %:</td>
                        <td class="font-weight-bold success--text text--darken-3 text-right">
                          {{ averageProfitPercentage }}%
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Snackbar -->
    <snackbar ref="snackbar"></snackbar>
  </v-container>
</template>

<script>
import "export-json-excel";
export default {
  data: () => ({
    dailySales: [],
    totalSales: 0,
    StartDate: new Date().toISOString().substr(0, 10),
    EndDate: new Date().toISOString().substr(0, 10),
    menu1: false,
    menu2: false,
    loader: false,
    headers: [
      { text: "Date", value: "Date", align: "left" },
      { text: "Customer", value: "CustomerName", align: "left" },
      { text: "Total (LC)", value: "ConvertedTotal", align: "left" },
      { text: "Cost of Products", value: "TotalGeneralCost", align: "left" },
      { text: "Margin", value: "Margin", align: "left" },
      { text: "Profit %", value: "ProfitPercentage", align: "left" }
    ],
    fields: {
      Date: "Date",
      Customer: "CustomerName",
      Total: "ConvertedTotal",
      CostOfProducts: "TotalGeneralCost",
      Margin: "Margin",
      ProfitPercentage: "ProfitPercentage"
    }
  }),
  computed: {
    totalProfitMargin() {
      return this.dailySales.reduce((sum, item) => {
        return sum + (item.ConvertedTotal - item.TotalGeneralCost);
      }, 0);
    },
    totalCost() {
      return this.dailySales.reduce((sum, item) => {
        return sum + item.TotalGeneralCost;
      }, 0);
    },
    averageProfitPercentage() {
      if (this.totalSales === 0) return '0.00';
      const percentage = (this.totalProfitMargin / this.totalSales) * 100;
      return percentage.toFixed(2);
    }
  },
  methods: {
    getRecords() {
      let data = {};
      data.StartDate = this.StartDate;
      data.EndDate = this.EndDate;
      data.combinedReport = "N";
      const self = this;

      const url = `/export-invoices`;

      this.loader = true;
      this.$store.commit("loader", true);
      this.$store
        .dispatch("post", { url, data })
        .then(res => {
          if (res.ResultCode == 1200) {
            self.dailySales = res.ResponseData.sales;

            // Calculate total sales
            self.totalSales = self.dailySales.reduce((sum, item) => {
              return sum + item.ConvertedTotal;
            }, 0);

            self.loader = false;
            self.$store.commit("loader", false);
          } else {
            this.$refs.snackbar.show(res.ResultDesc, "red");
            self.loader = false;
            self.$store.commit("loader", false);
          }
        })
        .catch(err => {
          this.$refs.snackbar.show(err, "red");
          self.loader = false;
          self.$store.commit("loader", false);
        });
    },
    onStartDateChange() {
      this.menu1 = false;
      this.getRecords();
    },
    onEndDateChange() {
      this.menu2 = false;
      this.getRecords();
    },
    calculateProfitPercentage(item) {
      if (!item.ConvertedTotal || item.ConvertedTotal === 0) return '0.00';
      const margin = item.ConvertedTotal - item.TotalGeneralCost;
      const percentage = (margin / item.ConvertedTotal) * 100;
      return percentage.toFixed(2);
    },
    getProfitColor(item) {
      const percentage = parseFloat(this.calculateProfitPercentage(item));
      if (percentage >= 30) return 'success';
      if (percentage >= 15) return 'warning';
      return 'error';
    }
  },
  created() {
    this.getRecords();
  }
};
</script>

<style lang="scss" scoped></style>
